import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getB2BDashboard } from '../Config/organizationService';
import { formatCentsAsCurrency } from '../Utils/money';
import { formatEasternDate } from '../Utils/dateTime';

function KpiCard({ title, value, subtitle, onClick, accentClassName = 'from-[#002F53] to-[#EF5E28]' }) {
  const content = (
    <>
      <div className={`h-1 bg-gradient-to-r ${accentClassName}`} />
      <div className="p-4 sm:p-5">
        <div className="text-sm font-medium text-gray-600">{title}</div>
        <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
        {subtitle ? <div className="mt-1 text-xs text-gray-500">{subtitle}</div> : null}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden text-left w-full hover:ring-[#EF5E28]/40 transition-shadow"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
      {content}
    </div>
  );
}

function Panel({ title, subtitle, children, right }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
      <div className="flex items-start justify-between gap-4 p-4 sm:p-5 border-b border-gray-100">
        <div>
          <div className="text-base font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="mt-1 text-sm text-gray-500">{subtitle}</div> : null}
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function B2BDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await getB2BDashboard();
        if (cancelled) return;
        if (!res?.success) {
          throw new Error(res?.message || 'Failed to load B2B dashboard');
        }
        setData(res.data);
      } catch (e) {
        if (cancelled) return;
        toast.error(e?.message || 'Failed to load B2B dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = data?.cards || {};
  const ordersByStatus = data?.orders_by_status || {};
  const recentOrgs = data?.recent_organizations || [];
  const recentOrders = data?.recent_orders || [];
  const topOrgs = data?.top_organizations_by_orders || [];

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">B2B Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">
              Organizations, portal members, and B2B order activity
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/organizations"
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
            >
              View organizations
            </Link>
            <Link
              to="/organizations/new"
              className="inline-flex items-center justify-center rounded-lg bg-[#EF5E28] px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-[#d54f1f]"
            >
              Create organization
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading...</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                title="Organizations"
                value={cards.organizations_total ?? 0}
                subtitle={`${cards.organizations_active ?? 0} active`}
                onClick={() => navigate('/organizations')}
              />
              <KpiCard
                title="Active members"
                value={cards.members_active ?? 0}
                subtitle="Portal users across orgs"
                accentClassName="from-[#002F53] to-sky-500"
              />
              <KpiCard
                title="Orders open"
                value={cards.orders_open ?? 0}
                subtitle={`${cards.orders_total ?? 0} total B2B`}
                onClick={() => navigate('/orders?channel=B2B')}
                accentClassName="from-amber-500 to-[#EF5E28]"
              />
              <KpiCard
                title="Pending quotes / charges"
                value={`${cards.pending_quotations ?? 0} / ${cards.pending_charges ?? 0}`}
                subtitle="Quotations · charges"
                onClick={() => navigate('/orders?channel=B2B')}
                accentClassName="from-violet-600 to-[#002F53]"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Panel title="Orders by status" subtitle="B2B channel">
                {Object.keys(ordersByStatus).length === 0 ? (
                  <p className="text-sm text-gray-500">No status breakdown yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {Object.entries(ordersByStatus).map(([status, count]) => (
                      <li key={status} className="flex items-center justify-between text-sm">
                        <button
                          type="button"
                          className="text-left text-gray-700 hover:text-[#EF5E28]"
                          onClick={() => navigate(`/orders?channel=B2B&status=${encodeURIComponent(status)}`)}
                        >
                          {status.replace(/_/g, ' ')}
                        </button>
                        <span className="font-semibold text-gray-900">{count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel
                title="Top organizations"
                subtitle="By order count"
                right={
                  <Link to="/organizations" className="text-sm font-medium text-[#EF5E28] hover:underline">
                    All
                  </Link>
                }
              >
                {topOrgs.length === 0 ? (
                  <p className="text-sm text-gray-500">No organizations yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {topOrgs.map((org) => (
                      <li key={org.organization_id}>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between py-2.5 text-left hover:bg-gray-50 -mx-1 px-1 rounded"
                          onClick={() => navigate(`/organizations/${org.organization_id}`)}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">{org.name}</div>
                            <div className="text-xs text-gray-500">
                              {org.is_active ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {org.order_count ?? 0}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel
                title="Recent organizations"
                right={
                  <Link to="/organizations/new" className="text-sm font-medium text-[#EF5E28] hover:underline">
                    New
                  </Link>
                }
              >
                {recentOrgs.length === 0 ? (
                  <p className="text-sm text-gray-500">No recent organizations.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {recentOrgs.map((org) => (
                      <li key={org.organization_id}>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between py-2.5 text-left hover:bg-gray-50 -mx-1 px-1 rounded"
                          onClick={() => navigate(`/organizations/${org.organization_id}`)}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">{org.name}</div>
                            <div className="text-xs text-gray-500">
                              {[org.city, org.is_active ? 'Active' : 'Inactive']
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 text-right">
                            {formatEasternDate(org.created_at)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <Panel
              title="Recent B2B orders"
              right={
                <Link
                  to="/orders?channel=B2B"
                  className="text-sm font-medium text-[#EF5E28] hover:underline"
                >
                  View all
                </Link>
              }
            >
              {recentOrders.length === 0 ? (
                <p className="text-sm text-gray-500">No recent B2B orders.</p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Order
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Organization
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Service
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {recentOrders.map((order) => (
                        <tr
                          key={order.order_id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => navigate(`/orders/${order.order_id}`)}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            #{order.order_id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {order.organization_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {order.service_title || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {(order.status || '').replace(/_/g, ' ')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatCentsAsCurrency(order.expected_amount)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatEasternDate(order.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        )}
      </main>
    </div>
  );
}

export default B2BDashboard;
